import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API, useAuth } from '@/App';
import FeeDisplay from './FeeDisplay';
import ContactInstitutionModal from './ContactInstitutionModal';
import { resolveImageUrl } from '@/utils/imageUrl';
import { CommentSection } from '@/components/CommentSection';
import { PostCard } from '@/pages/FeedPage';
import axios from 'axios';
import { MapPin, Users, Star, GraduationCap, BookOpen, Clock, Plus, Heart, Share2, ArrowLeft, Shield, Trophy, Building2, MessageCircle, Bookmark, MoreHorizontal, Eye, ChevronRight, Globe2, Award, Copy, Check, BadgeCheck, Flame, Sparkles } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import GuestGateModal from '@/components/GuestGateModal';
import { useGuestGate } from '@/hooks/useGuestGate';

/* ============================================================================
 * Visual tokens — kept inline (no new file) so the system theme stays in
 * App.css / index.css. Teal is the Unexa trust signal; blue is reserved for
 * action buttons; amber is the commercial/applied signal.
 * ========================================================================== */
const TEAL = '#1A7A6E';
const NAVY = '#0F2240';
const AMBER = '#F5A623';
const AMBER_DARK = '#b07a00';

/** Render-time apostrophe fix — backfills common dropped apostrophes in
 *  free-text fields like "Australia s leading provider" without mutating the
 *  database. Pure formatting, idempotent. */
const fixApostrophes = (s) => {
  if (!s || typeof s !== 'string') return s;
  return s.replace(/(\b\w+)\s+s\b/g, "$1's");
};

/** Per-field colour palette for course cards (image bg + left border).
 *  Falls back to navy for anything unrecognised. */
export const COURSE_FIELD_COLOR = {
  'architecture and building': '#92400E',
  'creative arts': '#7C3AED',
  'health': '#1A7A6E',
  'information technology': '#1E40AF',
  'management and commerce': '#B45309',
  'education': '#065F46',
  'food hospitality and personal services': '#DC2626',
  'society and culture': '#4338CA',
  'mixed field programmes': '#374151',
  'non aqf': '#6B7280',
};
export const getFieldColor = (field) => {
  if (!field) return NAVY;
  const k = String(field).replace(/^\d+\s*-\s*/, '').trim().toLowerCase();
  for (const key of Object.keys(COURSE_FIELD_COLOR)) {
    if (k.startsWith(key)) return COURSE_FIELD_COLOR[key];
  }
  return NAVY;
};

/** AQF level → pill palette + left-border colour. */
export const LEVEL_PALETTE = (degree) => {
  const d = String(degree || '').toLowerCase();
  if (d.includes('certificate')) return { border: TEAL,  bg: 'rgba(26,122,110,0.12)', text: TEAL };
  if (d.includes('diploma'))     return { border: '#2563EB', bg: 'rgba(37,99,235,0.12)', text: '#2563EB' };
  if (d.includes('bachelor'))    return { border: AMBER, bg: 'rgba(245,166,35,0.12)', text: AMBER_DARK };
  if (d.includes('graduate'))    return { border: '#7C3AED', bg: 'rgba(124,58,237,0.12)', text: '#7C3AED' };
  if (d.includes('master') || d.includes('doctor')) return { border: NAVY, bg: 'rgba(15,34,64,0.12)', text: NAVY };
  return { border: '#6B7280', bg: 'rgba(107,114,128,0.12)', text: '#6B7280' };
};

export function UniHeader({ uniData, following, followLoading, onToggleFollow, showBack = true, isOwnProfile = false, onApplyScholarship, hideApplyScholarship = false }) {
  const navigate = useNavigate();
  const { gate, sheetOpen, sheetTitle, setSheetOpen, returnUrl } = useGuestGate();
  const [copied, setCopied] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  if (!uniData) return null;

  // Build a sensible headline if the institution didn't provide one. Uses
  // existing `university_type`, `provider_type`, course count, and city.
  const buildHeadline = () => {
    if (uniData.tagline) return uniData.tagline;
    if (uniData.headline) return uniData.headline;
    const parts = [];
    if (uniData.university_type) parts.push(uniData.university_type.replace(/_/g, ' '));
    if (uniData.course_count) parts.push(`${uniData.course_count}+ courses`);
    if (uniData.city) parts.push(`based in ${uniData.city}`);
    if (parts.length) return parts.join(' · ');
    return 'Government-recognised education provider';
  };
  const headline = buildHeadline();

  // Block 4 — markets the institution welcomes (defaults to top student
  // source markets for AU institutions when not explicitly set).
  const acceptsCountries = uniData.accepts_countries || uniData.intl_countries || [
    { code: 'IN', flag: '🇮🇳', name: 'India' },
    { code: 'CN', flag: '🇨🇳', name: 'China' },
    { code: 'NP', flag: '🇳🇵', name: 'Nepal' },
    { code: 'PK', flag: '🇵🇰', name: 'Pakistan' },
    { code: 'BD', flag: '🇧🇩', name: 'Bangladesh' },
    { code: 'VN', flag: '🇻🇳', name: 'Vietnam' },
    { code: 'PH', flag: '🇵🇭', name: 'Philippines' },
  ];
  const visaSubclasses = uniData.visa_subclasses || ['500 (Student)', '485 (PSW)'];

  const copyCricos = () => {
    if (!uniData.cricos_code) return;
    navigator.clipboard?.writeText(uniData.cricos_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div data-testid="uni-header">
      {/* Cover */}
      <div className="relative h-40 bg-gradient-to-br from-blue-100 via-fuchsia-100 to-blue-50 overflow-hidden">
        {uniData.cover_image_url && (
          <img src={uniData.cover_image_url} alt="" className="w-full h-full object-cover opacity-60" />
        )}
      </div>

      {/* Uni Info */}
      <div className="px-4 pt-3 relative">
        <div className="flex items-center gap-3">
          <div
            className="w-[70px] h-[70px] rounded-xl bg-white border-2 border-slate-100 flex items-center justify-center overflow-hidden shrink-0 shadow-sm cursor-pointer"
            onClick={() => navigate(`/university/${uniData.university_id}`)}
          >
            {uniData.logo_url ? (
              <img src={uniData.logo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <Building2 className="h-7 w-7 text-[#2F80ED]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2
                className="text-xl font-bold font-heading truncate cursor-pointer transition-colors"
                style={{ color: NAVY }}
                onClick={() => navigate(`/university/${uniData.university_id}`)}
                data-testid="uni-header-name"
              >
                {uniData.name}
              </h2>
              <span
                className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                style={{ backgroundColor: TEAL, color: '#FFFFFF' }}
                data-testid="uni-verified-pill"
              >
                Verified
              </span>
              {uniData.provider_type === 'rpl_provider' && (
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-purple-100 text-purple-600 text-[10px] font-bold" data-testid="rpl-badge">RPL</span>
              )}
            </div>
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <span aria-hidden>📍</span>{uniData.city}, {uniData.country}
            </p>
          </div>
        </div>

        {/* Block 1 — Headline / Value prop */}
        <p className="mt-2.5 text-[13px] text-slate-600 leading-relaxed font-medium" data-testid="uni-headline">
          {fixApostrophes(headline)}
        </p>

        {/* Subtle teal divider separating identity from credentials */}
        <div className="h-px my-3" style={{ backgroundColor: 'rgba(26,122,110,0.15)' }} />

        {/* Block 3 — Trust & Credentials Bar */}
        <div className="flex flex-wrap items-center gap-1.5 mt-1" data-testid="uni-trust-bar">
          {uniData.cricos_code && (
            <button onClick={copyCricos}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md transition-colors group"
              style={{ backgroundColor: 'rgba(26,122,110,0.1)', border: `1px solid ${TEAL}`, color: TEAL }}
              data-testid="cricos-pill" title="Copy CRICOS code">
              <BadgeCheck className="h-3 w-3" style={{ color: TEAL }} />
              <span className="text-[10px] font-bold">CRICOS {uniData.cricos_code}</span>
              {copied ? <Check className="h-2.5 w-2.5" style={{ color: TEAL }} /> : <Copy className="h-2.5 w-2.5 opacity-60 group-hover:opacity-100" style={{ color: TEAL }} />}
            </button>
          )}
          {(uniData.university_type === 'government' || uniData.is_government) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold"
              style={{ backgroundColor: 'rgba(37,99,235,0.1)', border: '1px solid #2563EB', color: '#2563EB' }}
              data-testid="teqsa-pill">
              <Shield className="h-3 w-3" /> TEQSA Registered
            </span>
          )}
          {uniData.ranking_qs && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 border border-amber-100 text-[10px] font-bold text-amber-700" data-testid="ranking-pill">
              <Trophy className="h-3 w-3" /> QS #{uniData.ranking_qs}
            </span>
          )}
          {uniData.established_year && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-[10px] font-bold text-slate-600" data-testid="founded-pill">
              <Award className="h-3 w-3" /> Est. {uniData.established_year}
            </span>
          )}
          {uniData.campuses?.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold"
              style={{ backgroundColor: 'rgba(245,166,35,0.1)', border: `1px solid ${AMBER}`, color: AMBER_DARK }}
              data-testid="campuses-pill">
              <Building2 className="h-3 w-3" style={{ color: AMBER }} /> {uniData.campuses.length} campus{uniData.campuses.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        {/* Stats — bento cards (institution-grade commercial signals).
            Rating tile collapses to Campuses when there are no reviews — we
            never show an empty/dash rating. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="uni-stats">
          <div className="rounded-2xl border border-emerald-100 bg-white p-3" data-testid="stat-visits">
            <div className="flex items-center gap-1.5 text-emerald-500 text-[9px] uppercase tracking-widest font-bold">
              <Eye className="h-3 w-3" /> Visits
            </div>
            <div className="text-lg font-extrabold leading-tight mt-1 font-mono" style={{ color: TEAL }}>{(uniData.total_visits || 0).toLocaleString()}</div>
            <div className="text-[10px] text-emerald-600/70 mt-0.5">last 30 days</div>
          </div>
          {(uniData.review_count || 0) > 0 ? (
            <div className="rounded-2xl border border-amber-100 bg-white p-3" data-testid="stat-rating">
              <div className="flex items-center gap-1.5 text-amber-500 text-[9px] uppercase tracking-widest font-bold">
                <Star className="h-3 w-3 fill-amber-400" /> Rating
              </div>
              <div className="text-lg font-extrabold leading-tight mt-1 font-mono" style={{ color: AMBER }}>
                <span aria-hidden>⭐</span> {uniData.avg_rating}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{uniData.review_count} review{uniData.review_count !== 1 ? 's' : ''}</div>
            </div>
          ) : (
            <div className="rounded-2xl border border-amber-100 bg-white p-3" data-testid="stat-campuses">
              <div className="flex items-center gap-1.5 text-amber-500 text-[9px] uppercase tracking-widest font-bold">
                <Building2 className="h-3 w-3" /> Campuses
              </div>
              <div className="text-lg font-extrabold leading-tight mt-1 font-mono" style={{ color: AMBER }}>{uniData.campuses?.length || 1}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">across Australia</div>
            </div>
          )}
          <div className="rounded-2xl border border-fuchsia-100 bg-white p-3" data-testid="stat-courses">
            <div className="flex items-center gap-1.5 text-fuchsia-500 text-[9px] uppercase tracking-widest font-bold">
              <BookOpen className="h-3 w-3" /> Courses
            </div>
            <div className="text-lg font-extrabold leading-tight mt-1 font-mono" style={{ color: AMBER }}>{uniData.course_count || 0}</div>
            <div className="text-[10px] text-fuchsia-600/70 mt-0.5">across {uniData.campuses?.length || 1} campus{(uniData.campuses?.length || 1) !== 1 ? 'es' : ''}</div>
          </div>
          <div className="rounded-2xl border border-sky-100 bg-white p-3" data-testid="stat-verified">
            <div className="flex items-center gap-1.5 text-sky-500 text-[9px] uppercase tracking-widest font-bold">
              <Shield className="h-3 w-3" /> Verified
            </div>
            <div className="text-lg font-extrabold leading-tight mt-1 font-mono" style={{ color: TEAL }}>✓ Active</div>
            <div className="text-[10px] text-sky-600/70 mt-0.5">government registered</div>
          </div>
        </div>

        {/* Contact + Apply for Scholarship — stacked, matching course-detail style */}
        {!isOwnProfile && (
        <div className="space-y-2 mt-3">
          <button
            onClick={() => gate('Sign up to contact colleges', () => setShowContactModal(true))}
            className="w-full py-2.5 rounded-full text-sm font-bold bg-[#0A2540] text-white btn-glow transition-all active:scale-95 flex items-center justify-center gap-2"
            data-testid="contact-institution-btn"
          >
            <MessageCircle className="h-4 w-4" /> Contact Institution
          </button>
          {!hideApplyScholarship && (
            <button
              onClick={() => gate('Sign up to apply', () => onApplyScholarship?.())}
              className="w-full py-2.5 rounded-full bg-white border-2 border-amber-400 text-amber-700 text-sm font-bold active:scale-95 transition-all flex items-center justify-center gap-2 hover:bg-amber-50"
              data-testid="apply-scholarship-btn"
            >
              <Sparkles className="h-4 w-4" />
              Apply for Scholarship / Fee Discount
            </button>
          )}
        </div>
        )}
        {sheetOpen && (
          <GuestGateModal
            title={sheetTitle}
            returnUrl={returnUrl}
            onClose={() => setSheetOpen(false)}
          />
        )}

        {/* Block 4 — Country & Visa Eligibility */}
        <div className="mt-4 rounded-2xl bg-gradient-to-br from-blue-50 to-fuchsia-50 border border-blue-100 px-3 py-2.5" data-testid="uni-eligibility">
          <div className="flex items-center gap-2 mb-1.5">
            <Globe2 className="h-3.5 w-3.5 text-[#2F80ED]" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#2F80ED]">Welcomes students from</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {acceptsCountries.slice(0, 8).map(c => (
              <span key={c.code || c.name} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/80 text-[11px] font-semibold text-slate-700 border border-white/60"
                title={c.name}>
                <span>{c.flag}</span><span className="hidden sm:inline">{c.name}</span>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Visa subclasses:</span>
            {visaSubclasses.map(v => {
              // 500 (Student) → teal; 485 (PSW) → amber. Anything else → neutral.
              const code = String(v).match(/\d{3}/)?.[0];
              const isTeal = code === '500';
              const isAmber = code === '485';
              const style = isTeal
                ? { backgroundColor: 'rgba(26,122,110,0.1)', border: `1px solid ${TEAL}`, color: TEAL }
                : isAmber
                  ? { backgroundColor: 'rgba(245,166,35,0.1)', border: `1px solid ${AMBER}`, color: AMBER_DARK }
                  : { backgroundColor: 'rgba(15,23,42,0.04)', border: '1px solid #e2e8f0', color: '#475569' };
              return (
                <span key={v} className="text-[11px] font-mono font-bold px-2 py-1 rounded" style={style}>
                  {v}
                </span>
              );
            })}
          </div>
        </div>
      </div>
      {showContactModal && (
        <ContactInstitutionModal
          institution={uniData}
          onClose={() => setShowContactModal(false)}
        />
      )}
    </div>
  );
}

function StatItem({ icon: Icon, color, value, label, fill }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`h-4 w-4 ${color}`} fill={fill ? 'currentColor' : 'none'} />
      <span className="text-sm font-bold font-mono text-slate-700">{value}</span>
      <span className="text-xs text-slate-400">{label}</span>
    </div>
  );
}

export function UniTabBar({ activeTab, onTabChange }) {
  // Internal tab id remains `feed` (used in routing/state). The user-facing
  // label for institution profiles is "Posts" to read more naturally.
  const TAB_LABELS = { feed: 'Posts' };
  return (
    <div className="w-full border-b border-slate-200 mt-4">
      <div className="flex">
        {['about', 'courses', 'reviews', 'media', 'feed'].map(tab => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className="flex-1 py-3 text-sm capitalize font-medium border-b-2 transition-all"
              style={{
                borderBottomColor: isActive ? TEAL : 'transparent',
                color: isActive ? TEAL : '#94a3b8',
              }}
              data-testid={`tab-${tab}`}
            >
              {TAB_LABELS[tab] || tab}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function UniFeed({ universityName, logoUrl, isOwnProfile, universityId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState({});

  useEffect(() => {
    fetchPosts();
  }, [universityId]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // Find institution's user_id from posts API filtered by university
      const res = await axios.get(`${API}/posts?limit=50`);
      const allPosts = res.data.posts || res.data;
      const uniPosts = allPosts.filter(p => p.user_role === 'institution' && p.university_id === universityId);
      setPosts(uniPosts);
    } catch { }
    finally { setLoading(false); }
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/posts/${postId}/like`);
      setPosts(prev => prev.map(p => {
        if (p.post_id !== postId) return p;
        const likes = p.likes || [];
        return res.data.liked
          ? { ...p, likes: [...likes, user?.user_id], like_count: (p.like_count || 0) + 1 }
          : { ...p, likes: likes.filter(id => id !== user?.user_id), like_count: Math.max(0, (p.like_count || 0) - 1) };
      }));
    } catch { toast.error('Failed to like'); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    // Optimistic UI — instant card removal, restore on failure.
    const snapshot = posts;
    setPosts(prev => prev.filter(p => p.post_id !== postId));
    try {
      await axios.delete(`${API}/posts/${postId}`);
      toast.success('Post deleted');
    } catch (e) {
      setPosts(snapshot);
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // Save (bookmark) — was no-op'd to `() => {}`, fix it
  const handleBookmark = async (postId) => {
    if (!user) return;
    try {
      const res = await axios.post(`${API}/posts/${postId}/bookmark`, {});
      setPosts(prev => prev.map(p => p.post_id === postId
        ? { ...p, bookmarks: res.data.bookmarked
            ? [...(p.bookmarks || []), user.user_id]
            : (p.bookmarks || []).filter(b => b !== user.user_id) }
        : p));
      toast.success(res.data.bookmarked ? 'Saved' : 'Unsaved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleEditSave = (postId, newContent) => {
    setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, content: newContent } : p));
  };

  const toggleComments = (id) => {
    setCommentsOpen(p => ({ ...p, [id]: !p[id] }));
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="h-6 w-6 border-2 border-[#2F80ED] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-4 px-4 pt-3" data-testid="university-feed">
      {/* Create Post bar — only for institution owner */}
      {isOwnProfile && (
        <div
          className="feed-post p-3 flex items-center gap-3 cursor-pointer active:scale-[0.99] transition-transform"
          onClick={() => navigate('/create', { state: { from: `/university/${universityId}` } })}
          data-testid="uni-create-post-box"
        >
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0 overflow-hidden">
            {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-cover" /> : <Building2 className="h-5 w-5 text-[#2F80ED]" />}
          </div>
          <div className="flex-1 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center px-4">
            <span className="text-sm text-slate-400">What's on your mind?</span>
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle className="h-8 w-8 text-slate-200 mx-auto mb-2" />
          <p className="text-xs text-slate-400">No posts yet</p>
        </div>
      ) : (
        posts.map(p => (
          <PostCard
            key={p.post_id}
            post={p}
            onLike={() => handleLike(p.post_id)}
            onComment={() => toggleComments(p.post_id)}
            onBookmark={handleBookmark}
            onShare={() => {}}
            onRepost={() => {}}
            onDelete={handleDelete}
            onEditSave={handleEditSave}
            userId={user?.user_id}
            currentUserRole={user?.role || 'student'}
            hideProfileCTA={isOwnProfile}
          />
        ))
      )}
    </div>
  );
}

export function UniCourses({ courses, onAddBucket }) {
  const navigate = useNavigate();
  const [activeField, setActiveField] = useState(null);

  if (!courses?.length) {
    return <p className="text-center text-slate-400 py-10 text-sm">No courses listed yet</p>;
  }

  // Strip the ANZSCO `NN - ` prefix from field names so labels read as
  // "Natural and Physical Sciences (4)" rather than the raw category code.
  const cleanField = (f) => (f || 'Other').replace(/^\d+\s*-\s*/, '').trim();

  const grouped = {};
  courses.forEach(c => {
    const field = cleanField(c.field_of_study);
    if (!grouped[field]) grouped[field] = [];
    grouped[field].push(c);
  });
  const fields = Object.keys(grouped).sort();
  const displayFields = activeField ? [activeField] : fields;

  // Block 6 — Featured / Most Applied. HIDDEN GLOBALLY (Jun 18, 2026 — user
  // request: hide on all institution profile views until we can show signals
  // computed from real conversion data rather than the bachelor/fee heuristic).
  const featured = [];

  const fieldColors = {
    Arts: 'bg-pink-50 text-pink-600 border-pink-200',
    Engineering: 'bg-blue-50 text-blue-600 border-blue-200',
    Healthcare: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    Hospitality: 'bg-amber-50 text-amber-600 border-amber-200',
    'Computer Science': 'bg-blue-50 text-[#2F80ED] border-blue-200',
    'Data Science': 'bg-cyan-50 text-cyan-600 border-cyan-200',
    Business: 'bg-orange-50 text-orange-600 border-orange-200',
    'Information Technology': 'bg-indigo-50 text-indigo-600 border-indigo-200',
  };
  const defaultColor = 'bg-slate-50 text-slate-600 border-slate-200';

  return (
    <div className="px-4 pt-3">
      {/* Block 6 — Featured Courses (only on the All view) */}
      {featured.length > 0 && (
        <div className="mb-5" data-testid="featured-courses">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Flame className="h-3.5 w-3.5 text-orange-500" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">Most Applied</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {featured.map((course, idx) => {
              const lvl = LEVEL_PALETTE(course.degree_type);
              return (
              <div
                key={course.course_id}
                onClick={() => navigate(`/courses/${course.course_id}`)}
                className="relative rounded-2xl bg-white border border-slate-100 hover:border-orange-200 hover:shadow-md transition-all p-3 cursor-pointer group"
                style={{ borderLeft: `3px solid ${lvl.border}` }}
                data-testid={`featured-course-${course.course_id}`}
              >
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 text-white text-[9px] font-extrabold flex items-center justify-center shadow-sm">
                  {idx + 1}
                </div>
                <div className="flex flex-wrap gap-1 mb-1.5 pr-6">
                  {course.scholarship_available && (
                    <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">Scholarship</span>
                  )}
                  {course.degree_type && (
                    <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: lvl.bg, border: `1px solid ${lvl.border}`, color: lvl.text }}>
                      {course.degree_type}
                    </span>
                  )}
                </div>
                <h3 className="text-[13px] font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#2F80ED] transition-colors">
                  {course.name}
                </h3>
                <div className="flex items-center justify-between mt-2.5">
                  <div className="flex items-center gap-2 text-[10px] text-slate-500">
                    {course.duration_months && (
                      <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{course.duration_months}mo</span>
                    )}
                    {course.study_mode && <span>{course.study_mode}</span>}
                  </div>
                  {/* Jun 18 — single source of truth: FeeDisplay reads
                      `course.fee_display` from the server. Never show CRICOS. */}
                  <FeeDisplay course={course} amountSize="text-sm" />
                </div>
                <div className="mt-2 flex gap-1.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/courses/${course.course_id}`); }}
                    className="flex-1 py-1.5 rounded-lg bg-[#2F80ED] text-white text-[11px] font-bold hover:bg-[#1A6BD4] transition-colors"
                    data-testid={`featured-apply-${course.course_id}`}
                  >
                    Apply Now →
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onAddBucket(course.course_id); }}
                    className="px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                    data-testid={`featured-save-${course.course_id}`}
                    title="Save this course"
                  >
                    <Bookmark className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Field of Study Badges */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="field-badges">
        <button
          onClick={() => setActiveField(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${!activeField ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-100 border-slate-300 hover:border-[#1A7A6E] hover:text-[#1A7A6E]'}`}
          style={!activeField ? undefined : { color: NAVY }}
          data-testid="field-badge-all"
        >
          All ({courses.length})
        </button>
        {fields.map(f => {
          const isActive = activeField === f;
          return (
            <button
              key={f}
              onClick={() => setActiveField(isActive ? null : f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${isActive ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-100 border-slate-300 hover:border-[#1A7A6E] hover:text-[#1A7A6E]'}`}
              style={isActive ? undefined : { color: NAVY }}
              data-testid={`field-badge-${f}`}
            >
              {f} ({grouped[f].length})
            </button>
          );
        })}
      </div>

      {/* Course Groups */}
      <div className="space-y-2">
        {displayFields.map(field => (
          grouped[field] ? <PublicFieldGroup key={field} field={field} courses={grouped[field]} navigate={navigate} onAddBucket={onAddBucket} /> : null
        ))}
      </div>
    </div>
  );
}

function PublicFieldGroup({ field, courses, navigate, onAddBucket }) {
  const [open, setOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? courses : courses.slice(0, 3);
  const hasMore = courses.length > 3;

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 py-2 pl-2 group rounded-md"
        style={{ borderLeft: `3px solid ${TEAL}` }}
        data-testid={`public-field-${field}`}
      >
        <ChevronRight className="h-3.5 w-3.5 transition-transform" style={{ color: NAVY, transform: open ? 'rotate(90deg)' : 'rotate(0)' }} />
        <GraduationCap className="h-3.5 w-3.5" style={{ color: NAVY }} />
        <span className="text-sm font-bold" style={{ color: NAVY }}>{field}</span>
        <span
          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold"
          style={{ backgroundColor: 'rgba(26,122,110,0.1)', border: `1px solid ${TEAL}`, color: TEAL }}
        >
          {courses.length}
        </span>
        <span className="flex-1" />
        {hasMore && (
          <span
            onClick={(e) => { e.stopPropagation(); setShowAll(!showAll); }}
            className="text-[11px] font-bold px-2 py-0.5 rounded transition-colors hover:underline"
            style={{ color: TEAL }}
            data-testid={`show-all-${field}`}
          >
            {showAll ? 'Show Less' : `Show All (${courses.length})`}
          </span>
        )}
      </button>
      {open && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-2">
          {visible.map(course => <UniCourseCard key={course.course_id} course={course} navigate={navigate} onAddBucket={onAddBucket} />)}
        </div>
      )}
    </div>
  );
}

function UniCourseCard({ course, navigate, onAddBucket }) {
  const lvl = LEVEL_PALETTE(course.degree_type);
  const fieldColor = getFieldColor(course.field_of_study);
  return (
    <div
      onClick={() => navigate(`/courses/${course.course_id}`)}
      className="bg-white rounded-2xl border border-slate-100 overflow-hidden cursor-pointer hover:shadow-lg hover:shadow-slate-100/80 hover:-translate-y-0.5 transition-all duration-200 group relative"
      style={{ borderLeft: `3px solid ${lvl.border}` }}
      data-testid={`uni-course-${course.course_id}`}
    >
      {/* Header — solid colour per subject so categories are visually distinct */}
      <div className="h-28 flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: fieldColor }}>
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
        <BookOpen className="h-10 w-10 text-white drop-shadow-md" />
      </div>

      <div className="p-3 space-y-1.5 min-w-0">
        <div className="flex items-center justify-between gap-1.5 min-w-0">
          {course.degree_type ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
              style={{ backgroundColor: lvl.bg, border: `1px solid ${lvl.border}`, color: lvl.text }}>
              {course.degree_type}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 text-[10px] font-bold border border-slate-200">
              <BookOpen className="h-2.5 w-2.5" /> Course
            </span>
          )}
        </div>

        <h3 className="text-sm font-bold text-slate-800 line-clamp-2 leading-tight group-hover:text-[#2F80ED] transition-colors">
          {course.name}
        </h3>

        {course.field_of_study && (
          <p className="text-xs text-slate-500 line-clamp-1">{(course.field_of_study || '').replace(/^\d+\s*-\s*/, '').trim()}</p>
        )}

        <div className="flex items-center justify-between pt-1">
          <FeeDisplay course={course} amountSize="text-sm" />
          <button
            onClick={(e) => { e.stopPropagation(); onAddBucket(course.course_id); }}
            className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center text-[#2F80ED] hover:bg-blue-100 transition-colors active:scale-90"
            data-testid={`uni-course-save-${course.course_id}`}
            title="Save this course"
          >
            <Bookmark className="h-3 w-3" />
          </button>
        </div>

        {/* Apply Now — same blue style as Featured cards, full width, consistent CTA */}
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/courses/${course.course_id}`); }}
          className="w-full mt-2 py-1.5 rounded-lg bg-[#2F80ED] text-white text-[11px] font-bold hover:bg-[#1A6BD4] transition-colors"
          data-testid={`uni-course-apply-${course.course_id}`}
        >
          Apply Now →
        </button>
      </div>

      {course.scholarship_available && (
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold shadow-sm">Scholarship</span>
      )}
    </div>
  );
}

export function UniReviews({ universityId, reviews, avgRating, reviewCount }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitReview = async () => {
    if (!user) { navigate('/auth'); return; }
    if (!content.trim()) { toast.error('Write something!'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/${universityId}`, { rating, title, content });
      toast.success('Review posted!');
      setShowForm(false);
      setTitle(''); setContent(''); setRating(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 px-4 pt-3">
      <div className="glass-card p-4 flex items-center gap-4">
        <div className="text-center">
          <p className="text-3xl font-bold font-mono text-[#2F80ED]">{avgRating || '-'}</p>
          <div className="flex gap-0.5 mt-1">{[1,2,3,4,5].map(s => <Star key={s} className={`h-3 w-3 ${s <= Math.round(avgRating) ? 'text-amber-500 fill-current' : 'text-slate-200'}`} />)}</div>
          <p className="text-[10px] text-slate-400 mt-1">{reviewCount} reviews</p>
        </div>
        <div className="flex-1">
          <button onClick={() => { if (!user) navigate('/auth'); else setShowForm(!showForm); }} className="w-full py-2 rounded-full bg-blue-50 text-[#2F80ED] text-xs font-medium hover:bg-blue-100 transition-all active:scale-95" data-testid="write-review-btn">Write a Review</button>
        </div>
      </div>

      {showForm && (
        <div className="glass-card p-4 space-y-3 animate-slide-up">
          <div className="flex gap-1">{[1,2,3,4,5].map(s => <button key={s} onClick={() => setRating(s)} className="p-0.5"><Star className={`h-6 w-6 transition-colors ${s <= rating ? 'text-amber-500 fill-current' : 'text-slate-200'}`} /></button>)}</div>
          <input type="text" placeholder="Review title (optional)" className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm placeholder:text-slate-300 focus:outline-none focus:border-[#2F80ED]" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea placeholder="Share your experience..." className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm placeholder:text-slate-300 focus:outline-none focus:border-[#2F80ED] resize-none min-h-[80px]" value={content} onChange={(e) => setContent(e.target.value)} />
          <div className="flex gap-2">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2 rounded-full bg-slate-100 text-slate-400 text-xs">Cancel</button>
            <button onClick={submitReview} disabled={submitting} className="flex-1 py-2 rounded-full bg-[#2F80ED] text-white text-xs font-bold active:scale-95">{submitting ? '...' : 'Post Review'}</button>
          </div>
        </div>
      )}

      {reviews?.length > 0 ? reviews.map(review => (
        <div key={review.review_id} className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-[#2F80ED] text-xs font-bold">{review.user_name?.[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">{review.user_name}</p>
              <div className="flex items-center gap-1">{[1,2,3,4,5].map(s => <Star key={s} className={`h-2.5 w-2.5 ${s <= review.rating ? 'text-amber-500 fill-current' : 'text-slate-200'}`} />)}</div>
            </div>
            <span className="text-[10px] text-slate-300">{new Date(review.created_at).toLocaleDateString()}</span>
          </div>
          {review.title && <p className="text-sm font-semibold text-slate-800 mb-1">{review.title}</p>}
          <p className="text-xs text-slate-500 leading-relaxed">{review.content}</p>
        </div>
      )) : (
        <p className="text-center text-slate-300 text-sm py-8">No reviews yet. Be the first!</p>
      )}
    </div>
  );
}

export function UniAbout({ data }) {
  return (
    <div className="space-y-4 px-4 pt-3">
      {data.description && (
        <div className="glass-card p-4">
          <h3 className="text-sm font-semibold mb-2 text-slate-600">About</h3>
          <p className="text-sm text-slate-500 leading-relaxed">{data.description}</p>
        </div>
      )}
      <div className="glass-card p-4 space-y-2">
        <h3 className="text-sm font-semibold mb-2 text-slate-600">Basic Information</h3>
        {data.registration_number && <InfoRow label="Registration No." value={data.registration_number} />}
        {data.established_year && <InfoRow label="Founded" value={data.established_year} />}
        {data.university_type && <InfoRow label="Type" value={data.university_type} />}
        {data.provider_type === 'rpl_provider' && <InfoRow label="Category" value="RPL Institution" />}
        {data.total_students && <InfoRow label="Total Students" value={data.total_students?.toLocaleString()} />}
        {data.international_students_percent && <InfoRow label="International Students" value={`${data.international_students_percent}%`} />}
        {data.ranking_qs && <InfoRow label="QS Ranking" value={`#${data.ranking_qs}`} />}
        {data.ranking_times && <InfoRow label="THE Ranking" value={`#${data.ranking_times}`} />}
      </div>
      <div className="glass-card p-4 space-y-2">
        <h3 className="text-sm font-semibold mb-2 text-slate-600">Location</h3>
        {data.suburb && <InfoRow label="Suburb" value={data.suburb} />}
        <InfoRow label="City" value={data.city} />
        {data.state && <InfoRow label="State" value={data.state} />}
        <InfoRow label="Country" value={data.country} />
      </div>
      {(data.website || data.email || data.phone) && (
        <div className="glass-card p-4 space-y-2">
          <h3 className="text-sm font-semibold mb-2 text-slate-600">Contact</h3>
          {data.website && <InfoRow label="Website" value={<a href={data.website} target="_blank" rel="noreferrer" className="text-[#2F80ED] hover:underline">{data.website}</a>} />}
          {data.email && <InfoRow label="Email" value={data.email} />}
          {data.phone && <InfoRow label="Phone" value={data.phone} />}
        </div>
      )}
      {data.facilities?.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-sm font-semibold mb-3 text-slate-600">Facilities</h3>
          <div className="flex flex-wrap gap-2">{data.facilities.map(f => <span key={f} className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-500 text-xs">{f}</span>)}</div>
        </div>
      )}
      {data.accreditations?.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-sm font-semibold mb-3 text-slate-600">Accreditations</h3>
          <div className="flex flex-wrap gap-2">{data.accreditations.map(a => <span key={a} className="px-3 py-1.5 rounded-full bg-blue-50 text-[#2F80ED] text-xs">{a}</span>)}</div>
        </div>
      )}
    </div>
  );
}

export function UniMedia({ data }) {
  const photos = data.campus_photos || [];
  if (!photos.length) {
    return (
      <div className="text-center py-16 px-4">
        <Eye className="h-8 w-8 text-slate-200 mx-auto mb-2" />
        <p className="text-sm text-slate-400">No campus photos yet</p>
      </div>
    );
  }
  return (
    <div className="px-4 pt-3" data-testid="uni-media">
      <h3 className="text-sm font-semibold text-slate-600 mb-3">Campus Gallery</h3>
      <div className="grid grid-cols-2 gap-2">
        {photos.map((url, i) => (
          <div key={i} className="rounded-xl overflow-hidden aspect-video bg-slate-100">
            <img src={url} alt={`Campus ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-xs text-slate-600 font-medium">{value}</span>
    </div>
  );
}
